import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CalendarClock, Mail, Phone, User, Clock, StickyNote, AlertTriangle, FileText, Copy, RefreshCw, ShieldOff, ExternalLink, Send, CheckCircle2 } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { StatusBadge } from "@/components/StatusBadge";
import { ErrorState, Skeleton } from "@/components/BookingShell";
import { formatPrice, formatMoney, formatFullDate, formatDuration, paymentMethodLabel } from "@/lib/format";
import { ReadyEarlyButton } from "@/components/ReadyEarlyButton";
import { ReadinessPanel } from "@/components/ReadinessPanel";
import { CAT_LABEL } from "./Forms";
import { CommunicationsTimeline } from "./communications/Timeline";
import { PaymentPanel } from "./payments/PaymentPanel";
import { PhotographyConsentPanel } from "@/components/PhotographyConsentPanel";

import { BookingManagement } from "@/components/BookingManagement";
import StaffTimePicker from "@/components/StaffTimePicker";
import {VisitAppointment} from '@/components/VisitAppointment';

const STATUS_OPTIONS = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "canceled", label: "Cancelled" },
    { value: "rejected", label: "Rejected" },
    { value: "no-show", label: "No-show" },
];

export function StaffAppointmentDetail({ appointmentId, embedded = false, onChanged }) {
    const params = useParams();
    const id = appointmentId ?? params.id;
    const nav = useNavigate();
    const [appt, setAppt] = useState(null);
    const [manageVisit, setManageVisit] = useState(false);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [notes, setNotes] = useState("");
    const [rescheduleDate, setRescheduleDate] = useState("");
    const [rescheduleTime, setRescheduleTime] = useState("");
    const [showReschedule, setShowReschedule] = useState(false);
    const [confirmCancel, setConfirmCancel] = useState(null); // status the user clicked, pending confirmation for destructive ones

    const reload = () => {
        setError(null);
        staffApi.getAppointment(id).then((a) => { setAppt(a); setNotes(a.internalNotes || ""); onChanged?.(); }).catch(setError);
    };
    useEffect(reload, [id, onChanged]);

    // Amelia only recognises the US spelling. Never send "cancelled".
    const DESTRUCTIVE = new Set(["canceled", "rejected", "no-show"]);

    const applyStatus = async (s) => {
        if (busy) return;
        setBusy(true);
        try {
            await staffApi.changeStatus(id, s);
            toast.success(`Status changed to ${s}`);
            setConfirmCancel(null);
            reload();
        } catch (e) {
            toast.error(e.message || "Could not update status");
        } finally { setBusy(false); }
    };

    const changeStatus = (s) => {
        if (busy) return;
        if (DESTRUCTIVE.has(s)) { setConfirmCancel(s); return; }
        applyStatus(s);
    };

    const saveNotes = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await staffApi.updateNotes(id, notes);
            toast.success("Notes saved");
            reload();
        } catch (e) { toast.error(e.message || "Could not update notes"); }
        finally { setBusy(false); }
    };

    const doReschedule = async () => {
        if (!rescheduleDate || !rescheduleTime) return;
        if (busy) return;
        setBusy(true);
        try {
            await staffApi.reschedule(id, rescheduleDate, rescheduleTime);
            toast.success("Appointment rescheduled");
            setShowReschedule(false);
            reload();
        } catch (e) { toast.error(e.message || "Reschedule could not be completed"); }
        finally { setBusy(false); }
    };

    if (!embedded && (manageVisit || appt?.use_visit || (appt?.visit_appointment_ids || []).length > 1)) return <VisitAppointment appointmentId={id}/>;
    return (
        <div className="min-w-0 w-full space-y-6" data-testid="staff-appointment-detail">
            {!embedded && <button onClick={() => nav(-1)} data-testid="back-button" className="text-xs text-white/50 hover:text-gold flex items-center gap-1">
                <ArrowLeft className="h-3 w-3" /> Back
            </button>}

            {error && <ErrorState error={error} onRetry={reload} />}
            {!appt && !error && <Skeleton className="h-64" />}

            {appt && (
                <>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-gold/85">Appointment #{appt.id}</div>
                        {!embedded && <h1 className="mt-2 font-serif font-light text-4xl text-white leading-[1.05]" data-testid="appt-customer-name">
                            {appt.customer.firstName} {appt.customer.lastName}
                        </h1>}
                        <div className="mt-2 flex items-center gap-3">
                            {appt.outcome_label ? <span className="rounded-full border border-gold px-3 py-1 text-gold" data-testid="appointment-outcome">{appt.outcome_label}</span> : <StatusBadge status={appt.status} />}
                            {!appt.outcome_label && appt.payment?.status && <span className="text-[10px] uppercase tracking-[0.18em] text-white/50">payment: {appt.payment.status}</span>}
                        </div>
                        {!appt.outcome_label && (appt.status || "").toLowerCase() !== "checked-out" && (
                            <div className="mt-4 flex flex-wrap items-center gap-2">
                                {!embedded && <Link to={`/staff/appointments/${appt.id}/checkout`} data-testid="btn-checkout"
                                      className="inline-flex items-center gap-2 rounded-full bg-gold text-navy px-5 h-10 font-semibold text-sm hover:opacity-90">
                                    Check out this appointment
                                </Link>}
                                <ReadyEarlyButton appointment={appt} variant="detail" />
                            </div>
                        )}
                    </div>

                    <ReadinessPanel appointment={appt} onPaymentRecorded={reload} clinicalOnly={embedded} />

                    <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="min-w-0 w-full rounded-2xl glass p-5 space-y-3" data-testid="appt-info">
                            <Row icon={CalendarClock} label="When" value={`${formatFullDate((appt.bookingStart || "").slice(0,10))} · ${(appt.bookingStart||"").slice(11,16)}`} />
                            <Row icon={Clock} label="Service" value={`${appt.serviceName || `#${appt.serviceId}`}`} />
                            <Row icon={User} label="Practitioner" value="Angela" />
                            <Row icon={Mail} label="Email" value={appt.customer.email || "—"} />
                            <Row icon={Phone} label="Phone" value={appt.customer.phone || "—"} />
                            {embedded && appt.customerId && <Link to={`/staff/customers/${appt.customerId}`} className="text-sm text-gold underline">View customer →</Link>}
                        </div>
                        {!embedded && <div className="min-w-0 w-full rounded-2xl glass p-5 space-y-3" data-testid="appt-money">
                            {(() => {
                                const price = Number(appt.price || 0);
                                const paid = Number(appt.payment?.amount || 0);
                                const isPaid = (appt.payment?.status || "").toLowerCase() === "paid";
                                const outstanding = Math.max(price - (isPaid ? paid : 0), 0);
                                return (
                                    <>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Treatment price</div>
                                            <div className="mt-1 font-serif text-3xl gold-text" data-testid="money-price">{formatPrice(price)}</div>
                                        </div>
                                        <div className="grid min-w-0 grid-cols-2 gap-3 pt-2 [overflow-wrap:anywhere]">
                                            <div>
                                                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Amount paid</div>
                                                <div className="mt-1 text-white font-ui" data-testid="money-paid">{formatMoney(isPaid ? paid : 0)}</div>
                                            </div>
                                            <div>
                                                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Outstanding</div>
                                                <div className="mt-1 text-white font-ui" data-testid="money-outstanding">{formatMoney(outstanding)}</div>
                                            </div>
                                            <div>
                                                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Payment status</div>
                                                <div className="mt-1 text-white font-ui capitalize" data-testid="money-status">{appt.payment?.status || "—"}</div>
                                            </div>
                                            <div>
                                                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Payment method</div>
                                                <div className="mt-1 text-white font-ui" data-testid="money-method">
                                                    {appt.payment?.gatewayTitle || paymentMethodLabel(appt.payment?.gateway, appt.payment?.status)}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                            {appt.customerId && (
                                <Link to={`/staff/customers/${appt.customerId}`} className="text-xs text-gold hover:underline">View customer →</Link>
                            )}
                        </div>}
                    </div>

                    {!embedded && <PaymentPanel key={`${appt.id}-${appt.payment?.amount || 0}`} appointmentId={appt.id} appt={appt} onSynced={reload} />}
                    {!embedded && <button className="rounded-full bg-gold text-navy px-5 py-3 font-semibold" onClick={()=>setManageVisit(true)}>Appointment payments, refunds and credit</button>}
                    <PhotographyConsentPanel appointmentId={appt.id} />

                    <CommunicationsTimeline scope="appointment" id={appt.id} title="Communications timeline" />

                    {/* Status actions */}
                    <div className="rounded-2xl glass p-5" data-testid="appt-status-actions">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-3">Change status</div>
                        <div className="flex flex-wrap gap-2">
                            {STATUS_OPTIONS.map((s) => (
                                <button
                                    key={s.value}
                                    onClick={() => changeStatus(s.value)}
                                    disabled={busy || (appt.status || "").toLowerCase() === s.value}
                                    data-testid={`status-btn-${s.value}`}
                                    className={`text-xs uppercase tracking-[0.18em] px-4 py-2 rounded-full border ${
                                        (appt.status || "").toLowerCase() === s.value
                                            ? "bg-gold text-navy border-gold"
                                            : "border-white/15 text-white/70 hover:border-gold/50 hover:text-gold"
                                    } disabled:opacity-40`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                        <p className="mt-3 text-[11px] text-white/40">
                            Status changes save immediately. If a change is rejected, you'll see an error toast.
                        </p>
                    </div>

                    <BookingManagement appointment={appt} onSaved={reload} onDeleted={()=>nav(`/staff/calendar?date=${(appt.bookingStart || "").slice(0,10)}`)} />
                    {/* Reschedule */}
                    {!appt.deleted && <div className="rounded-2xl glass p-5" data-testid="appt-reschedule-block">
                        <div className="flex items-center justify-between">
                            <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Reschedule</div>
                            {!showReschedule && (
                                <button onClick={() => { setShowReschedule(true); setRescheduleDate((appt.bookingStart||"").slice(0,10)); setRescheduleTime((appt.bookingStart||"").slice(11,16)); }} data-testid="open-reschedule" className="text-xs text-gold hover:underline">
                                    Change date/time
                                </button>
                            )}
                        </div>
                        {showReschedule && (
                            <div className="mt-3 flex flex-wrap items-end gap-3">
                                <label className="text-xs text-white/60">
                                    Date
                                    <input type="date" value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} data-testid="reschedule-date" className="mt-1 block bg-transparent border hairline rounded-md px-3 py-2 text-white text-sm" />
                                </label>
                                <label className="text-xs text-white/60">
                                    Time
                                    <StaffTimePicker value={rescheduleTime} onChange={setRescheduleTime} label="Appointment time" />
                                </label>
                                <button onClick={doReschedule} disabled={busy} data-testid="reschedule-submit" className="h-10 rounded-full bg-gold text-navy px-5 text-sm font-semibold disabled:opacity-40">Save</button>
                                <button onClick={() => setShowReschedule(false)} className="h-10 rounded-full border hairline text-white/60 px-5 text-sm">Cancel</button>
                            </div>
                        )}
                    </div>

                    }
                    {/* Notes */}
                    <div className="rounded-2xl glass p-5" data-testid="appt-notes-block">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-2 flex items-center gap-2">
                            <StickyNote className="h-3 w-3" /> Internal notes
                        </div>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            data-testid="appt-notes"
                            rows={4}
                            className="w-full bg-transparent border hairline rounded-2xl p-3 text-white text-sm outline-none focus:border-gold"
                        />
                        <button onClick={saveNotes} disabled={busy} data-testid="save-notes" className="mt-2 text-xs text-gold hover:underline">
                            Save notes
                        </button>
                    </div>

                    {/* Destructive-status confirmation modal */}
                    {confirmCancel && (
                        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4" data-testid="confirm-cancel-modal">
                            <div className="w-full max-w-md rounded-3xl glass-strong p-6">
                                <div className="flex min-w-0 items-start gap-3">
                                    <div className="h-10 w-10 rounded-full glass flex items-center justify-center border border-amber-500/40">
                                        <AlertTriangle className="h-5 w-5 text-amber-300" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <h3 className="font-serif text-xl text-white">
                                            Set status to <span className="text-gold">{confirmCancel}</span>?
                                        </h3>
                                        <p className="mt-2 text-sm text-white/60">
                                            This will update the booking status and trigger the matching customer notification if the automation is enabled.
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-6 flex gap-2 justify-end">
                                    <button onClick={() => setConfirmCancel(null)} data-testid="confirm-cancel-abort" className="h-10 rounded-full border hairline text-white/70 px-4 text-sm">Keep as is</button>
                                    <button onClick={() => applyStatus(confirmCancel)} disabled={busy} data-testid="confirm-cancel-confirm" className="h-10 rounded-full bg-gold text-navy px-4 text-sm font-semibold disabled:opacity-40">
                                        {busy ? "Updating..." : `Yes, set to ${confirmCancel}`}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function Row({ icon: Icon, label, value }) {
    return (
        <div className="flex min-w-0 items-start gap-3">
            <div className="mt-0.5 h-8 w-8 shrink-0 rounded-full glass flex items-center justify-center"><Icon className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} /></div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">{label}</div>
                <div className="text-white font-ui text-sm whitespace-normal [overflow-wrap:anywhere]">{value}</div>
            </div>
        </div>
    );
}

/* ==========================================================================
 * ApptFormsPanel — Stage 2 owner UI: prepare package, share link, view subs
 * ======================================================================= */
function ApptFormsPanel({ appt }) {
    const [pkg, setPkg] = useState(undefined);        // undefined = loading, null = none yet
    const [subs, setSubs] = useState([]);
    const [busy, setBusy] = useState(false);
    const [viewSubId, setViewSubId] = useState(null);

    const load = async () => {
        try {
            const r = await staffApi.getPackage(appt.id);
            setPkg(r.package || null);
            setSubs(r.submissions || []);
        } catch (_) { setPkg(null); }
    };
    useEffect(() => { if (appt?.id) load(); /* eslint-disable-next-line */ }, [appt?.id]);

    const publicUrl = pkg?.token ? `${window.location.origin}/f/${pkg.token}` : null;

    const doPrepare = async () => {
        setBusy(true);
        try {
            const r = await staffApi.preparePackage(appt.id, {
                customer_id: appt.customerId,
                service_id: appt.serviceId,
                appointment_start: appt.bookingStart,
                customer_first_name: appt.customer?.firstName || "",
                service_name: appt.serviceName || "",
            });
            if (r.message) toast.info(r.message);
            await load();
        } catch (e) { toast.error(e?.message || "Prepare failed"); }
        finally { setBusy(false); }
    };
    const doCopy = async () => {
        if (!publicUrl) return;
        try { await navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); }
        catch (_) { window.prompt("Copy link:", publicUrl); }
    };
    const doAction = async (action) => {
        if (action === "revoke" && !window.confirm("Revoke this link? The customer will no longer be able to open it.")) return;
        setBusy(true);
        try { await staffApi.packageAction(pkg.id, action); await load(); }
        catch (e) { toast.error(e?.message || `${action} failed`); }
        finally { setBusy(false); }
    };

    if (pkg === undefined) return <Skeleton className="h-24" />;
    return (
        <div className="min-w-0 w-full rounded-2xl glass p-5 space-y-3" data-testid="appt-forms-panel">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-gold" />
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Consultation & consent forms</div>
                </div>
                {!pkg && (
                    <button onClick={doPrepare} disabled={busy || !appt.customerId} data-testid="appt-prepare-forms"
                            className="h-9 px-3 rounded-full bg-gold text-navy font-semibold text-[11px] uppercase tracking-[0.14em] disabled:opacity-40 inline-flex items-center gap-1">
                        <Send className="h-3 w-3" /> Prepare customer forms
                    </button>
                )}
            </div>

            {!pkg && (
                <p className="text-xs text-white/50">
                    Generate a secure link the customer can open on their phone. The exact form versions in effect right now are frozen into the package — later edits never affect completed submissions.
                </p>
            )}
            {pkg && (
                <>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${
                            pkg.status === "active" ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-200"
                                : "bg-red-500/10 border-red-400/40 text-red-200"}`} data-testid="appt-pkg-status">
                            {pkg.status}
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.14em] text-white/45">Expires {pkg.expires_at ? new Date(pkg.expires_at).toLocaleDateString("en-GB") : "—"}</span>
                        <span className="text-[10px] uppercase tracking-[0.14em] text-white/45">· {pkg.overall_completion.replace("_"," ")}</span>
                    </div>
                    {pkg.status === "active" && publicUrl && (
                        <div className="rounded-lg border hairline bg-white/[0.04] p-2 flex items-center gap-2 min-w-0">
                            <code className="flex-1 min-w-0 truncate text-[11px] text-white/80 font-mono" data-testid="appt-pkg-link">{publicUrl}</code>
                            <button onClick={doCopy} data-testid="appt-copy-link" className="h-8 px-2 rounded-full border hairline text-xs text-white/80 hover:text-gold inline-flex items-center gap-1"><Copy className="h-3 w-3" /> Copy</button>
                            <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="appt-open-link" className="h-8 px-2 rounded-full border hairline text-xs text-white/60 hover:text-gold inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Open</a>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                        <button onClick={() => doAction("regenerate")} disabled={busy} data-testid="appt-regen-link"
                                className="h-8 px-3 rounded-full border hairline text-[11px] uppercase tracking-[0.14em] text-white/70 hover:text-gold inline-flex items-center gap-1">
                            <RefreshCw className="h-3 w-3" /> Regenerate link
                        </button>
                        {pkg.status === "active" && (
                            <button onClick={() => doAction("revoke")} disabled={busy} data-testid="appt-revoke-link"
                                    className="h-8 px-3 rounded-full border hairline text-[11px] uppercase tracking-[0.14em] text-red-300/80 hover:border-red-300/50 inline-flex items-center gap-1">
                                <ShieldOff className="h-3 w-3" /> Revoke link
                            </button>
                        )}
                    </div>
                    <div className="rounded-lg border hairline bg-white/[0.02] p-2 space-y-2">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-white/45">Assigned forms</div>
                        {pkg.assigned_form_ids.length === 0 && <p className="text-xs text-white/50">No forms in this package.</p>}
                        {pkg.assigned_form_ids.map((tid) => {
                            const sub = subs.find((s) => s.form_template_id === tid);
                            return (
                                <div key={tid} className="flex items-center justify-between text-xs gap-2" data-testid={`appt-form-row-${tid}`}>
                                    <div className="min-w-0 flex items-center gap-2">
                                        {sub ? <CheckCircle2 className="h-4 w-4 text-emerald-300 shrink-0" /> : <Clock className="h-4 w-4 text-white/40 shrink-0" />}
                                        <span className="text-white/85 truncate">{sub?.title_snapshot || tid.slice(0,8)}</span>
                                        <span className="text-white/40">v{pkg.frozen_versions[tid]?.slice(0,6)}</span>
                                    </div>
                                    {sub ? (
                                        <button onClick={() => setViewSubId(sub.id)} data-testid={`appt-view-sub-${sub.id}`} className="text-gold hover:underline text-[11px]">View submission</button>
                                    ) : (
                                        <span className="text-white/40 text-[11px]">Awaiting customer</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
            {viewSubId && <SubmissionModal id={viewSubId} onClose={() => setViewSubId(null)} />}
        </div>
    );
}

function SubmissionModal({ id, onClose }) {
    const [sub, setSub] = useState(null);
    const [sig, setSig] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        staffApi.getSubmission(id).then((r) => {
            setSub(r.submission);
            if (r.submission?.signature_id) staffApi.getSignature(r.submission.signature_id).then((s) => setSig(s));
        }).catch((e) => setError(e?.message || "Load failed"));
    }, [id]);
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-3 overflow-y-auto" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl my-6 rounded-2xl glass-strong border hairline p-5 space-y-3" data-testid="submission-modal">
                {error && <ErrorState error={new Error(error)} />}
                {!sub && !error && <Skeleton className="h-32" />}
                {sub && (
                    <>
                        <div className="flex items-baseline justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.18em] text-white/45">Immutable submission</div>
                                <h3 className="font-serif text-white text-xl break-words">{sub.title_snapshot}</h3>
                            </div>
                            <button onClick={onClose} className="text-white/60 text-sm">Close</button>
                        </div>
                        <div className="text-[11px] text-white/45">
                            Submitted {new Date(sub.submitted_at).toLocaleString("en-GB")} · {sub.timezone || "—"} · Version {sub.version_id?.slice(0,8)} · Audit ip# {sub.audit?.ip_hash || "—"}
                        </div>
                        <div className="rounded-xl border hairline p-3 bg-white/[0.02] space-y-4 max-h-[60vh] overflow-y-auto">
                            {(sub.sections_snapshot || []).map((s) => (
                                <div key={s.id}>
                                    <h4 className="font-serif text-white text-base">{s.title}</h4>
                                    <div className="mt-1 space-y-2">
                                        {(s.fields || []).filter((f) => !["section_heading","informational_text"].includes(f.kind)).map((f) => {
                                            const v = sub.answers?.[f.id];
                                            return (
                                                <div key={f.id} className="text-xs">
                                                    <div className="text-white/50">{f.label}</div>
                                                    <div className="text-white break-words">
                                                        {f.kind === "signature" ? "(see signature below)" :
                                                         Array.isArray(v) ? v.join(", ") :
                                                         v === true ? "Yes" : v === false ? "No" :
                                                         (v ?? "—")}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                            {sig?.image_base64 && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.14em] text-white/45 mb-1">Signature captured {sig.signed_at && new Date(sig.signed_at).toLocaleString("en-GB")}</div>
                                    <img alt="signature" src={sig.image_base64} className="max-h-32 bg-white/5 rounded-md border hairline p-2" data-testid="submission-signature" />
                                </div>
                            )}
                        </div>
                        <p className="text-[10px] text-white/40">
                            This submission is preserved exactly as the customer signed it. Editing a form later creates a new version — completed submissions are never modified.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

